/**
 * Date formatting utilities for consistent date display across the application.
 */

import { formatDistanceToNow } from 'date-fns';
import { ja } from 'date-fns/locale';

/**
 * Format a date string to Japanese locale date format.
 * Example: "2026年1月11日"
 */
export function formatDate(dateStr: string | null): string {
  if (!dateStr) return '未活動';
  const date = new Date(dateStr);
  return date.toLocaleDateString('ja-JP', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

/**
 * Format a date string to Japanese locale date and time format.
 * Example: "2026年1月11日 14:30"
 */
export function formatDateTime(dateStr: string | null): string {
  if (!dateStr) return '未活動';
  const date = new Date(dateStr);
  return date.toLocaleString('ja-JP', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Format a date string to a relative time string in Japanese.
 * Examples: "今日", "1日前", "2週間前", "1ヶ月前"
 */
export function formatRelativeTime(dateStr: string | null): string {
  if (!dateStr) return '未活動';

  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return '今日';
  if (diffDays === 1) return '1日前';
  if (diffDays < 7) return `${diffDays}日前`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)}週間前`;
  return `${Math.floor(diffDays / 30)}ヶ月前`;
}

/**
 * Format a week start date to a short label.
 * Example: "1/11"
 */
export function formatWeekLabel(dateStr: string): string {
  const date = new Date(dateStr);
  return `${date.getMonth() + 1}/${date.getDate()}`;
}

/**
 * Format a date string to a human-readable relative time using date-fns.
 * Examples: "5分前", "約1時間前", "3日前"
 */
export function formatTimeAgo(dateStr: string): string {
  try {
    return formatDistanceToNow(new Date(dateStr), {
      addSuffix: true,
      locale: ja,
    });
  } catch {
    return dateStr;
  }
}

/**
 * Format a date string to a detailed relative time with minute/hour precision.
 * Examples: "今", "5分前", "3時間前", "2日前", or date format for older dates.
 * @param fallbackToDate - If true, returns date format (e.g., "1月11日") for dates older than 7 days.
 *                         If false, continues with "X日前" format.
 */
export function formatDetailedRelativeTime(
  dateStr: string,
  options: { fallbackToDate?: boolean; includeTime?: boolean } = {}
): string {
  const { fallbackToDate = true, includeTime = false } = options;
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffMins < 1) return 'たった今';
  if (diffMins < 60) return `${diffMins}分前`;
  if (diffHours < 24) return `${diffHours}時間前`;

  if (fallbackToDate && diffDays >= 7) {
    if (includeTime) {
      return date.toLocaleString('ja-JP', {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    }
    return date.toLocaleDateString('ja-JP', {
      month: 'short',
      day: 'numeric',
    });
  }

  return `${diffDays}日前`;
}
